import React from "react";
import { useLocation } from "react-router-dom";
import classnames from "classnames";
import type { ApolloClient } from "@apollo/client";
import { push } from "store/actions/router";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { connect, type ConnectedProps } from "react-redux";
import {
  FiArrowLeftCircle,
  FiChevronDown,
  FiPlusCircle,
  FiX,
} from "react-icons/fi";
import { Form, OverlayTrigger, Popover } from "react-bootstrap";
import { debounce, get, includes, isEmpty, lowerCase, map, size } from "lodash";
import type { DebouncedFunc } from "lodash";

import { setEmployerSession } from "actions/employerActions";
import { toast } from "react-toastify";
import "./EmployerSwitcher.scss";
import { AGENT_ROLE_ID } from "statics/roles";
import type { RootState } from "types/state";

/** Minimal shape the component reads off each company. */
interface Company {
  id: string;
  name: string;
}

const mapStateToProps = (state: RootState) => {
  return {
    // employer slice models linkedCompanies/company shallowly (unknown[] / object) —
    // cast at the boundary to the fields this component reads.
    companies: state.employer.linkedCompanies as Company[],
    activeCompany: state.employer.company as unknown as Company,
    // get() over a string path is `unknown`; this prop is compared to a string literal.
    companyState: get(state.user, "companyState.state") as string | undefined,
    isEmployerAgent: state.user.userRoleId === AGENT_ROLE_ID,
    // organization slice is typed `object`; companyLimit is read at the boundary.
    organizationCompanyLimit: (
      state.employer.organization as { companyLimit?: number }
    ).companyLimit as number,
  };
};

const mapDispatchToProps = {
  setEmployerSession,
  push,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

// A parent passes nothing — every prop comes from redux, withApollo, or the
// useLocation wrapper below. Empty object (not Record<string, never>, whose
// `never` index signature would clash with the `pathname` added below).
type EmployerSwitcherOwnProps = Record<never, never>;

type EmployerSwitcherProps = PropsFromRedux & EmployerSwitcherOwnProps;

// `pathname` is injected by EmployerSwitcher_WithLocation (useLocation), not a parent.
type EmployerSwitcherClassProps = WithApolloClient<EmployerSwitcherProps> & {
  pathname: string;
};

interface EmployerSwitcherState {
  dropdownOpen: boolean;
  searchTerm: string;
  filteredCompanies: Company[];
}

class EmployerSwitcher extends React.PureComponent<
  EmployerSwitcherClassProps,
  EmployerSwitcherState
> {
  _debouncedFilter: DebouncedFunc<(searchTerm: string) => void>;

  constructor(props: EmployerSwitcherClassProps) {
    super(props);

    this.state = {
      dropdownOpen: false,
      searchTerm: "",
      filteredCompanies: [],
    };

    this._debouncedFilter = debounce((searchTerm: string) => {
      const filteredCompanies = this.props.companies.filter((company) =>
        includes(lowerCase(company.name), lowerCase(searchTerm))
      );
      this.setState({ filteredCompanies });
    }, 300);
  }

  _handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const searchTerm = e.target.value;

    this.setState({ searchTerm });
    this._debouncedFilter(searchTerm);
  };

  _clearSearch = () => {
    this._debouncedFilter.cancel();
    this.setState({ searchTerm: "", filteredCompanies: [] });
  };

  _getActionButton = () => {
    // I am adding an employer if I'm on onboarding route and my current company state is active.
    if (this.props.isEmployerAgent) return null;
    const onOnboardingRoute = includes(this.props.pathname, "employer/");
    const isAddingEmp =
      onOnboardingRoute &&
      this.props.companyState === "EmployerApplicationApproved";

    if (isAddingEmp) {
      return (
        <li
          className="action-item add-company-btn"
          onClick={() => {
            document.body.click();

            this.props.push("/dashboard");
          }}
        >
          <FiArrowLeftCircle size={14} fill="white" />
          Back to Dashboard
        </li>
      );
    }
    const lessThanLimit =
      size(this.props.companies) < this.props.organizationCompanyLimit;

    if (lessThanLimit) {
      return (
        <li
          className="action-item add-company-btn"
          onClick={() => {
            document.body.click();

            this.props.push("/employer/enroll");
          }}
        >
          <FiPlusCircle size={14} />
          Create Company
        </li>
      );
    }
  };
  _buildPopover = () => {
    const isScrollable = size(this.props.companies) > 5;
    const companiesToDisplay =
      this.state.searchTerm.length > 0
        ? this.state.filteredCompanies
        : this.props.companies;

    return (
      <Popover className="action-list-popover employer-switcher-dropdown">
        <Popover.Body
          style={{
            maxHeight: isScrollable ? "200px" : "auto",
            overflowY: isScrollable ? "auto" : "visible",
          }}
        >
          {isScrollable && (
            <div style={{ padding: "8px 8px 4px", position: "relative" }}>
              <Form.Control
                type="text"
                placeholder="Search companies..."
                value={this.state.searchTerm}
                onChange={this._handleSearchChange}
                size="sm"
                autoFocus
                style={{ paddingRight: this.state.searchTerm ? "30px" : "8px" }}
              />
              {this.state.searchTerm && (
                <div
                  onClick={this._clearSearch}
                  style={{
                    position: "absolute",
                    right: "16px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    color: "#6c757d",
                  }}
                >
                  <FiX size={16} />
                </div>
              )}
            </div>
          )}
          <ul className="actions-list">
            {this._getActionButton()}
            {map(companiesToDisplay, (company) => {
              return (
                <li
                  key={company.id}
                  className={classnames("action-item", {
                    ["is-selected"]: this.props.activeCompany.id === company.id,
                  })}
                  // eslint-disable-next-line @typescript-eslint/no-misused-promises -- pre-existing fire-and-forget async handler, not changed in conversion
                  onClick={async () => {
                    const result = await this.props.setEmployerSession(
                      this.props.client as ApolloClient<object>,
                      company.id
                    );
                    if (!result.success) {
                      toast.error(
                        "There was a problem switching companies. Please try again."
                      );
                      return;
                    }
                    window.location.search = `?redirect_to=/dashboard`;
                  }}
                >
                  {company.name}
                </li>
              );
            })}
          </ul>
        </Popover.Body>
      </Popover>
    );
  };
  _toggleDropdown = () => {
    this.setState({
      dropdownOpen: !this.state.dropdownOpen,
    });
  };

  render() {
    if (isEmpty(this.props.activeCompany)) return null;
    return (
      <div className="employer-switcher">
        <OverlayTrigger
          rootClose
          trigger="click"
          placement="bottom"
          overlay={this._buildPopover()}
          onToggle={(show) => {
            if (!show) {
              this._debouncedFilter.cancel();
              this.setState({ searchTerm: "", filteredCompanies: [] });
            }
          }}
        >
          <div className="target-box">
            <p className="active-company-name">
              {this.props.activeCompany.name}
              <span className="down-arrow">
                <FiChevronDown size={14} />
              </span>
            </p>
          </div>
        </OverlayTrigger>
      </div>
    );
  }
}

const EmployerSwitcher_WithLocation = (
  props: WithApolloClient<EmployerSwitcherProps>
) => {
  const { pathname } = useLocation();
  return <EmployerSwitcher {...props} pathname={pathname} />;
};

export default connector(
  withApollo<EmployerSwitcherProps>(EmployerSwitcher_WithLocation)
);
